import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import ErrorView from '../ErrorView';

import {
  useDeleteUserEmployeeRoleMutation,
  useEditUserEmployeeRoleMutation,
  useGetAllSystemAccessRightsQuery,
  useGetUserEmployeeRoleByIdQuery,
  useGetUserRoleAccessRightsByUserRoleIdQuery,
} from '../../Features/usersSlice';

import {
  ShieldCheckIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  TrashIcon,
  SaveIcon,
} from '@heroicons/react/outline';

function EditUserEmployeeRoleView() {
  const navigate = useNavigate();
  const { role_id } = useParams();
  const { isOnline } = useIsOnline();
  const user = useSelector(selectCurrentUser);

  const [progress, setProgress] = useState(0);

  const {
    data: roleData,
    isLoading: isLoadingRole,
    isSuccess: isSuccessRole,
    isError: isErrorRole,
  } = useGetUserEmployeeRoleByIdQuery(role_id);

  const {
    data: access_roles,
    isLoading: isLoadingAccessRoles,
    isSuccess: isSuccessAccessRoles,
    isError: isErrorAccessRoles,
  } = useGetAllSystemAccessRightsQuery();

  const {
    data: userRoleAccessRights,
    isLoading: isLoadingAccessRights,
    isSuccess: isSuccessAccessRights,
  } = useGetUserRoleAccessRightsByUserRoleIdQuery(role_id);

  const [input, setInput] = useState({
    access_rights: [],
  });

  const [nameInput, setNameInput] = useState({
    name: '',
  });

  const [errors, setErrors] = useState({
    name: '',
    access_rights: '',
  });

  useEffect(() => {
    if (isSuccessRole && roleData?.data) {
      setNameInput({
        name: roleData.data.name,
      });
    }
  }, [isSuccessRole, roleData]);

  useEffect(() => {
    if (isSuccessAccessRights && userRoleAccessRights?.data) {
      const assignedRights = userRoleAccessRights.data.map((r) =>
        String(r.access_right_id)
      );

      setInput((prev) => ({
        ...prev,
        access_rights: assignedRights,
      }));
    }
  }, [isSuccessAccessRights, userRoleAccessRights]);

  const handleNameChange = (e) => {
    setNameInput({
      name: e.target.value,
    });

    setErrors((prev) => ({
      ...prev,
      name: '',
    }));
  };

  const handleChange = (e) => {
    const { value, checked } = e.target;

    setInput((prev) => ({
      ...prev,
      access_rights: checked
        ? [...prev.access_rights, value]
        : prev.access_rights.filter((id) => id !== value),
    }));

    setErrors((prev) => ({
      ...prev,
      access_rights: '',
    }));
  };

  const [editUserEmployeeRole, { isLoading: isSaving }] =
    useEditUserEmployeeRoleMutation();

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setProgress(80);

      const payload = {
        user_id: user.id,
        role_id: role_id,
        name: nameInput.name,
        access_rights: input.access_rights,
      };

      const resp = await editUserEmployeeRole(payload).unwrap();

      if (resp) {
        setProgress(100);
        navigate(-1);
      }
    } catch (err) {
      setProgress(100);

      if (err?.data?.errors) {
        setErrors(err.data.errors);
      } else {
        swal('Error', 'Something went wrong', 'error');
      }
    }
  };

  const [deleteUserRole, { isLoading: isDeleting }] =
    useDeleteUserEmployeeRoleMutation();

  const handDelete = async (e) => {
    e.preventDefault();

    if (!isOnline) {
      return swal(
        'No Internet connection',
        'Please check Internet connection and try again'
      );
    }

    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        setProgress(80);

        const resp = await deleteUserRole(role_id).unwrap();

        if (resp.hasOwnProperty('success') === true) {
          setProgress(100);
          return navigate(-1);
        }
      } catch (err) {
        setProgress(100);
        swal('Error', err.message, 'error');
      }
    }
  };

  const back = () => {
    navigate(-1);
  };

  const isLoading =
    isLoadingRole ||
    isLoadingAccessRoles ||
    isLoadingAccessRights;

  const hasError = isErrorRole || isErrorAccessRoles;

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />

      {/* Header */}
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <ShieldCheckIcon className="w-6 h-6 text-[#80b643]" />
              </div>

              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  Edit Employee Role
                </h1>

                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Update the role name and access permissions
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-8 mt-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-10 h-10 border-4 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin" />

                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                  Loading role details...
                </p>
              </div>
            ) : hasError ? (
              <div className="p-6">
                <ErrorView />
              </div>
            ) : (
              <>
                {/* Role Information */}
                {isSuccessRole && (
                  <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                        <ShieldCheckIcon className="w-5 h-5 text-[#80b643]" />
                      </div>

                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Role Information
                        </h2>

                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Set the name for this employee role
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Role Name
                      </label>

                      <input
                        type="text"
                        name="name"
                        value={nameInput.name}
                        onChange={handleNameChange}
                        placeholder="Enter role name"
                        className={`w-full px-4 py-3 rounded-lg border ${
                          errors.name
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 dark:border-gray-700 focus:ring-[#80b643] focus:border-[#80b643]'
                        } bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 outline-none transition`}
                      />

                      {errors.name && (
                        <p className="mt-2 text-sm text-red-500">
                          {errors.name}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Access Rights */}
                {isSuccessAccessRoles && (
                  <form onSubmit={handleSubmit}>
                    <div className="p-5 sm:p-6">
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <CheckCircleIcon className="w-5 h-5 text-[#80b643]" />
                          </div>

                          <div>
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                              Access Rights
                            </h2>

                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Select the permissions assigned to this role
                            </p>
                          </div>
                        </div>

                        <div className="hidden sm:block">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#80b643]/10 text-[#80b643]">
                            {input.access_rights.length} selected
                          </span>
                        </div>
                      </div>

                      <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-800">
                          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            Available Permissions
                          </span>
                        </div>

                        <div className="divide-y divide-gray-200 dark:divide-gray-800">
                          {access_roles?.data?.length > 0 ? (
                            access_roles.data.map((data) => {
                              const isChecked =
                                input.access_rights.includes(
                                  String(data.id)
                                );

                              return (
                                <label
                                  key={data.id}
                                  className={`flex items-center justify-between gap-4 px-4 sm:px-5 py-4 cursor-pointer transition ${
                                    isChecked
                                      ? 'bg-[#80b643]/5 dark:bg-[#80b643]/10'
                                      : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div
                                      className={`w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center ${
                                        isChecked
                                          ? 'bg-[#80b643]/10 dark:bg-[#80b643]/20'
                                          : 'bg-gray-100 dark:bg-gray-800'
                                      }`}
                                    >
                                      <CheckCircleIcon
                                        className={`w-5 h-5 ${
                                          isChecked
                                            ? 'text-[#80b643]'
                                            : 'text-gray-400 dark:text-gray-500'
                                        }`}
                                      />
                                    </div>

                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                                      {data.name}
                                    </span>
                                  </div>

                                  <input
                                    type="checkbox"
                                    value={data.id}
                                    checked={isChecked}
                                    onChange={handleChange}
                                    className="w-5 h-5 flex-shrink-0 rounded border-gray-300 text-[#80b643] focus:ring-[#80b643] cursor-pointer"
                                  />
                                </label>
                              );
                            })
                          ) : (
                            <div className="py-10 text-center">
                              <div className="w-12 h-12 mx-auto rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                <ShieldCheckIcon className="w-6 h-6 text-gray-400" />
                              </div>

                              <p className="mt-3 text-sm font-medium text-gray-600 dark:text-gray-300">
                                No access rights found
                              </p>

                              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                                There are currently no permissions available.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {errors.access_rights && (
                        <p className="mt-2 text-sm text-red-500">
                          {errors.access_rights}
                        </p>
                      )}

                      {/* Delete Role */}
                      <div className="mt-6 border border-red-200 dark:border-red-900/50 rounded-xl overflow-hidden">
                        <div className="p-4 sm:p-5 bg-red-50/60 dark:bg-red-950/20">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <TrashIcon className="w-5 h-5 text-red-500" />
                              </div>

                              <div>
                                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                  Delete Role
                                </h3>

                                <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                  This will permanently delete this employee
                                  role.
                                </p>
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={handDelete}
                              disabled={isDeleting}
                              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 font-medium text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isDeleting ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <TrashIcon className="w-4 h-4" />
                                  Delete Role
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="px-5 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/30">
                      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                        <button
                          type="button"
                          onClick={back}
                          disabled={isSaving || isDeleting}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowLeftIcon className="w-4 h-4" />
                          Cancel
                        </button>

                        <button
                          type="submit"
                          disabled={isSaving || isDeleting}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white font-medium text-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {isSaving ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <SaveIcon className="w-4 h-4" />
                              Save Changes
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditUserEmployeeRoleView;